import { useEffect } from 'react';
import { useField } from '@shopify/react-form';
import { PersonaPickerOption } from '@sdk/components/forms/personaPickers/PersonaPicker/personaPicker.types';
import { RestrictionType } from '@sdk/messages/types/RestrictionType';
import gettext from '@sdk/lang/gettext';
import { useSession } from '@sdk/session/SessionContext';
import { UserCanStartConversationOption } from '@sdk/authentication/types';

import { useNavigate } from '@web/Router/router.hooks';
import { useGroupConversationsPremiumGuard } from '@web/conversations/hooks/useGroupConversationsPremiumGuard';

export const useRestrictedToUsersField = (
  restricted: RestrictionType,
  initialValue: PersonaPickerOption[]
) =>
  useField(
    {
      value: initialValue,
      validates: (selectedUsers) => {
        if (restricted === RestrictionType.SELECTED_ONLY && !selectedUsers?.length) {
          return gettext('Please select members');
        }
      },
    },
    [restricted]
  );

export const useConversationCreateRestrictions = () => {
  const { currentUser } = useSession();
  const { usersCanStartConversation } = currentUser.orgSettings;
  const { areGroupConversationsEnabled } = useGroupConversationsPremiumGuard();

  return {
    canStartPrivateConversation:
      currentUser.isManager || usersCanStartConversation !== UserCanStartConversationOption.none,
    canStartGroupConversation:
      currentUser.isManager ||
      (usersCanStartConversation === UserCanStartConversationOption.all &&
        areGroupConversationsEnabled),
    canOnlyStartPrivateConversation:
      !currentUser.isManager &&
      (usersCanStartConversation === UserCanStartConversationOption.privateOnly ||
        !areGroupConversationsEnabled),
  };
};

export const useCreatePrivateConversationRedirect = () => {
  const { currentUser } = useSession();
  const { areGroupConversationsEnabled } = useGroupConversationsPremiumGuard();
  const navigate = useNavigate();

  useEffect(() => {
    if (!areGroupConversationsEnabled && !currentUser.isManager) {
      navigate.toConversationComposePrivate();
    }
  }, [areGroupConversationsEnabled, currentUser.isManager, navigate]);
};
